// src/App.js
import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import GroupsList from "./pages/GroupsList";
import GroupDetail from "./pages/GroupDetail";
import GroupSpace from "./pages/GroupSpace";
import GroupAdmin from "./pages/GroupAdmin";
import CreateGroup from "./pages/CreateGroup";
import UserSettings from "./pages/UserSettings";

import { AuthProvider, useAuth } from "@/lib/auth/AuthProvider";
import LandingPage from "./components/landingPage/LandingPage";
import TermsPage from "./components/termandconditions/TermsPage";
import PrivacyPage from "./components/termandconditions/PrivacyPage";
import CookiesPage from "./components/termandconditions/CookiesPage";

import ZiiChatController from "./components/chatWidget/ZiiChatController";

// ✅ Initialize i18next (JSON loader)
import "./i18n";

import "./App.css";

// ----------------------------
// Private Route Wrapper
// ----------------------------
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;

  return user ? children : <Navigate to="/auth" replace />;
};

// ----------------------------
// Routes
// ----------------------------
function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />

      {/* Protected */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <UserSettings />
          </PrivateRoute>
        }
      />

      <Route
        path="/groups"
        element={
          <PrivateRoute>
            <GroupsList />
          </PrivateRoute>
        }
      />

      <Route
        path="/groups/create"
        element={
          <PrivateRoute>
            <CreateGroup />
          </PrivateRoute>
        }
      />

      <Route
        path="/groups/:groupId"
        element={
          <PrivateRoute>
            <GroupDetail />
          </PrivateRoute>
        }
      />

      <Route
        path="/groups/:groupId/space"
        element={
          <PrivateRoute>
            <GroupSpace />
          </PrivateRoute>
        }
      />

      <Route
        path="/groups/:groupId/admin"
        element={
          <PrivateRoute>
            <GroupAdmin />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

// ----------------------------
// Main App Component
// ----------------------------
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Suspense is required for JSON translation loading */}
        <Suspense fallback={<div>Loading translations...</div>}>
          <div className="App">
            <AppRoutes />

            {/* ZiiHub AI assistant */}
            <ZiiChatController />

            <Toaster position="top-right" />
          </div>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
